import { ReactNode } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  Skeleton,
  Switch,
} from '@tinct/web-components';
import { ReadSsoConnection, SsoConnectionStatus } from '@tinct/core-api-models';
import { IconCircleCheckFilled, IconCircleDashed } from '@tabler/icons-react';
import { format } from 'date-fns';
import { SECURITY_CARD_ANCHORS } from '../security-card-anchors';

/** How far the connection has come, in the order the admin walks it. */
const STEP_ORDER: SsoConnectionStatus[] = [
  'draft',
  'tested',
  'enabled',
  'enforced',
];

/**
 * The state's own name, as the notices of the identity provider card and the help center use it
 * ("sends the connection back to draft"). Members see the softer wording of SSO_STATE_COPY instead.
 */
const STATUS_BADGE: Record<
  SsoConnectionStatus,
  { label: string; variant: 'secondary' | 'success' | 'important' }
> = {
  draft: { label: 'Draft', variant: 'secondary' },
  tested: { label: 'Tested', variant: 'secondary' },
  enabled: { label: 'Enabled', variant: 'success' },
  enforced: { label: 'Required', variant: 'important' },
};

const isReached = (
  status: SsoConnectionStatus | null,
  step: SsoConnectionStatus,
) => !!status && STEP_ORDER.indexOf(status) >= STEP_ORDER.indexOf(step);

export type SsoStatusCardProps = {
  connection: ReadSsoConnection | undefined;
  isLoading: boolean;
  hasVerifiedDomain: boolean;
  isTesting: boolean;
  isUpdatingEnablement: boolean;
  onTest: () => void;
  onEnable: () => void;
  onDisable: () => void;
  /** Opens the confirmation dialog; `true` requires single sign-on, `false` lifts the requirement. */
  onEnforcementChange: (enforced: boolean) => void;
};

type StepProps = {
  title: string;
  description: string;
  done: boolean;
  action?: ReactNode;
};

const Step = ({ title, description, done, action }: StepProps) => (
  <div className="flex items-start justify-between gap-4 border-t py-4 first:border-t-0 first:pt-0">
    <div className="flex items-start gap-3">
      {done ? (
        <IconCircleCheckFilled className="mt-0.5 size-5 shrink-0 text-teal-600" />
      ) : (
        <IconCircleDashed className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
      )}
      <div className="flex flex-col gap-0.5">
        <span className="font-semibold">{title}</span>
        <span className="text-sm text-muted-foreground">{description}</span>
      </div>
    </div>
    {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
  </div>
);

/**
 * The four states of the connection, each with the one action that leaves it: describe the provider,
 * test the sign-in, enable it, require it. The API refuses out-of-order moves, so what is not this
 * step's action is not offered.
 */
export function SsoStatusCard({
  connection,
  isLoading,
  hasVerifiedDomain,
  isTesting,
  isUpdatingEnablement,
  onTest,
  onEnable,
  onDisable,
  onEnforcementChange,
}: SsoStatusCardProps) {
  const status = connection?.status ?? null;
  const isConfigured = !!connection?.id;
  const isEnforced = status === 'enforced';
  const lastTestedBy = connection?.lastTestedBy?.email;

  return (
    <Card id={SECURITY_CARD_ANCHORS.ssoStatus} tabIndex={-1}>
      <CardHeader>
        <div className="flex items-center gap-3">
          <CardTitle className="text-xl">Status</CardTitle>
          {!isLoading && status && (
            <Badge variant={STATUS_BADGE[status].variant}>
              {STATUS_BADGE[status].label}
            </Badge>
          )}
        </div>
        <CardDescription>
          Single sign-on goes live in steps, so a misconfigured provider never
          locks anybody out.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col">
        {isLoading ? (
          <Skeleton className="h-48 w-full rounded-xl" />
        ) : (
          <>
            <Step
              title="Identity provider described"
              description={
                !isConfigured
                  ? 'Fill in the identity provider card above to start.'
                  : status === 'draft'
                    ? 'Saved as a draft: run a test sign-in to go on.'
                    : 'Saved. Changing how your provider is trusted (issuer, client ID; for SAML the entity ID, sign-on URL, or leaving none of the certificates that were valid) sends the connection back to draft. A new client secret or an added certificate does not: run a test sign-in to check it.'
              }
              done={isConfigured}
            />
            <Step
              title="Test sign-in succeeded"
              description={
                connection?.lastTestedAt
                  ? `Last successful test on ${format(new Date(connection.lastTestedAt), 'dd/MM/yyyy')}${
                      lastTestedBy ? ` by ${lastTestedBy}` : ''
                    }.`
                  : 'Signs you in against your provider in a new window, without signing anybody in or creating anyone.'
              }
              done={isReached(status, 'tested')}
              action={
                (status === 'draft' || status === 'tested') && (
                  <Button
                    variant={status === 'draft' ? 'default' : 'outline'}
                    onClick={onTest}
                    isLoading={isTesting}
                  >
                    Test sign-in
                  </Button>
                )
              }
            />
            <Step
              title="Single sign-on enabled"
              description={
                hasVerifiedDomain
                  ? 'Addresses on your verified domains may sign in through your provider — a password still works.'
                  : 'Verify one e-mail domain first: a connection only authenticates domains you own.'
              }
              done={isReached(status, 'enabled')}
              action={
                status === 'tested' ? (
                  <Button
                    onClick={onEnable}
                    disabled={!hasVerifiedDomain}
                    isLoading={isUpdatingEnablement}
                  >
                    Enable
                  </Button>
                ) : (
                  status === 'enabled' && (
                    <Button
                      variant="outline"
                      onClick={onDisable}
                      isLoading={isUpdatingEnablement}
                    >
                      Disable
                    </Button>
                  )
                )
              }
            />
            <Step
              title="Single sign-on required"
              description={
                isEnforced
                  ? `Required since ${
                      connection?.enforcedAt
                        ? format(new Date(connection.enforcedAt), 'dd/MM/yyyy')
                        : 'it was switched on'
                    }: your provider is the only way in for your verified domains.`
                  : 'Makes your provider the only way in for every address on your verified domains.'
              }
              done={isEnforced}
              action={
                <>
                  <Label htmlFor="sso-required" className="sr-only">
                    Require single sign-on
                  </Label>
                  <Switch
                    id="sso-required"
                    checked={isEnforced}
                    disabled={status !== 'enabled' && !isEnforced}
                    onCheckedChange={onEnforcementChange}
                  />
                </>
              }
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
